'use client';

import { useEffect, useRef } from 'react';
import { useGetCsrfTokenMutation } from '@/api/features/auth';
import { cacheCsrfTokenForOnboarding, extractCsrfTokenFromResponse, hasCsrfCookie } from './csrfHelpers';

/**
 * Prefetch a CSRF token for onboarding pages only.
 * - Reads the `csrf-token` cookie; if missing, calls the auth RTK mutation for `/api/csrf-token`.
 * - On success, also populates/updates a `<meta name="csrf-token" />` tag for clients that read it.
 * - No impact on the global dashboard API client.
 */
export function useOnboardingCsrf(): void {
  const fetchedRef = useRef(false);
  const [getCsrfToken] = useGetCsrfTokenMutation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (fetchedRef.current) return;

    if (hasCsrfCookie()) {
      fetchedRef.current = true;
      return;
    }

    const prefetch = async () => {
      try {
        const result = await getCsrfToken().unwrap();
        const token = extractCsrfTokenFromResponse(result);
        cacheCsrfTokenForOnboarding(token);
      } catch {
        // best-effort only
      } finally {
        fetchedRef.current = true;
      }
    };

    prefetch();
  }, [getCsrfToken]);
}


